import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { environment } from '@env/environment';

import { HttpHelper } from '@app/core/helpers/http.hepler';
import { BtrPassportModel, PassportFilterType } from '@app/core/models';
import {
  IBtrEventListGetRequest,
  IBtrPassportApiModel,
} from '@app/core/models/api';
import {
  IBtrPassportInfoApiModel,
  ICheckInResponseApiModel,
} from '@app/core/models/api/btr-passport-check-in-info.api.model';
import { BtrPassportCheckInInfoModel } from '@app/core/models/btr-passport-check-in-info.model';
import { ISubscriptionRequest } from '@app/pages/reminder-subscription/components/models/subscription.model';

@Injectable()
export class BtrPassportApiService {
  constructor(private readonly http: HttpClient) {}

  getPassport(
    req: { id: string } | { slug: string },
    withBeacons: boolean = false,
  ): Observable<BtrPassportModel> {
    let params = new HttpParams({ fromObject: req });
    if (withBeacons) {
      params = params.append('with_beacons', '1');
    }
    return this.http
      .get<IBtrPassportApiModel>(`${environment.API_URL}v5/event.json`, {
        params,
      })
      .pipe(map(res => new BtrPassportModel(res)));
  }

  getPassports(
    data: IBtrEventListGetRequest,
  ): Observable<Array<BtrPassportModel>> {
    let params = new HttpParams({ fromObject: data as any });
    return this.http
      .get<Array<IBtrPassportApiModel>>(
        `${environment.API_URL}v5.1/events.json?filters=passport`,
        {
          params,
        },
      )
      .pipe(map(res => res.map(item => new BtrPassportModel(item))));
  }

  getClusterPassports(
    clusterId: number,
    search: string = '',
    filters?: PassportFilterType,
  ): Observable<Array<BtrPassportModel>> {
    if (!clusterId) {
      return of([]);
    }
    const params: IBtrEventListGetRequest = {
      cluster: clusterId,
      sort: 'start',
      sort_order: 'ASC',
      output_fields: ['beacons', 'tickets'].join(','),
    };
    if (filters?.length > 0 && filters !== PassportFilterType.NONE) {
      params.filters = filters;
    }
    if (search?.length > 2) {
      return this.getPassports({
        ...params,
        search,
      });
    }
    return this.getPassports(params);
  }

  getPassportCheckInInfo(
    token: string,
  ): Observable<BtrPassportCheckInInfoModel> {
    return this.http
      .get<IBtrPassportInfoApiModel>(
        `${environment.API_URL}v5/checkin/${token}/info.json`,
      )
      .pipe(map(res => new BtrPassportCheckInInfoModel(res)));
  }

  checkIn(token: string): Observable<ICheckInResponseApiModel> {
    return this.http.get<ICheckInResponseApiModel>(
      `${environment.API_URL}v5/checkin/${token}/`,
    );
  }

  answerToCheckInAttendee(token: string, formData: FormData): Observable<any> {
    return this.http
      .post<any>(`${environment.API_URL}v5/checkin/${token}/form/`, formData, {
        headers: HttpHelper.setHeaders(),
      })
      .pipe(map(res => res));
  }

  resubscribeForReminders(request: ISubscriptionRequest): Observable<any> {
    let params = new HttpParams();
    const neededRoute = request.checkId
      ? `userticket/${request.checkId}`
      : `whitelabel/${request.wlId}`;
    if (request.code?.length > 0) {
      params = params.append('code', request.code);
    }
    return this.http.get<any>(
      `${environment.API_URL}v5/subscribe/${neededRoute}/`,
      { params },
    );
  }

  unsubscribeForReminders(request: ISubscriptionRequest): Observable<any> {
    let params = new HttpParams();
    const neededRoute = request.checkId
      ? `userticket/${request.checkId}`
      : `whitelabel/${request.wlId}`;
    if (request.code?.length > 0) {
      params = params.append('code', request.code);
    }
    return this.http.get<any>(
      `${environment.API_URL}v5/unsubscribe/${neededRoute}/`,
      { params },
    );
  }
}
